// Vendor telat tanpa uang muka — Keputusan Owner 26 Sep 2026 (BUSINESS-53).
//
// Dua bagian: (1) PO yang janji kirimnya sudah lewat (mulai H+1) — Admin Purchasing menghubungi vendor, dan
// mulai hari kerja ke-8 sesudah janji kirim boleh mengajukan batal dari halaman detail PO (VONIS-27SEP-JAWABAN-10
// butir 7); PO yang sudah menerima sebagian → yang dibatalkan hanya sisanya (tutup sebagian, butir 8); (2) keterlambatan per vendor —
// HITUNGAN saja (PO dinilai, PO telat, total & terlama hari telat, batal karena telat). Bukan skor: rumus skor
// vendor belum pernah diputuskan. Wewenang baca dari /meta/po (baca_telat); backend yang menjaga.
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { StatusBadge } from "../../components/etams/StatusBadge";
import { Clock } from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "../../lib/format";
import { daftarPOTelat, keterlambatanVendor, apiErrorMessage } from "../../lib/purchasingApi";
import { useDaftar } from "../../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../../components/etams/PalangDaftar";

// POLA DAFTAR SERAGAM (28 Sep 2026; docs/POLA_DAFTAR.md) — MODE KLIEN, dan itu disengaja:
// kedua endpoint menghitung SELURUH himpunannya di Python (hari kerja, libur per region,
// pengajuan batal) dan mengembalikannya utuh — bukan potongan. PO telat = bagian dari PO
// yang masih berjalan; baris per vendor ≤ jumlah vendor. Cari/urut/halaman di browser,
// posisinya tersimpan di alamat dengan awalan sendiri (`tl_`, `tv_`) supaya tidak
// bertabrakan dengan daftar PO di layar yang sama.
const KOLOM_TELAT = [
  { kunci: "po_number", judul: "PO" },
  { kunci: "vendor_name", judul: "Vendor" },
  { kunci: "janji_kirim", judul: "Janji kirim", ambil: (t) => formatDate(t.janji_kirim), nilai: (t) => t.janji_kirim },
  { kunci: "hari_telat", judul: "Hari telat" },
  { kunci: "hari_kerja_telat", judul: "Hari kerja telat" },
];
const KOLOM_VENDOR = [
  { kunci: "vendor_name", judul: "Vendor" },
  { kunci: "po_dinilai", judul: "PO dinilai" },
  { kunci: "po_telat", judul: "PO telat" },
  { kunci: "total_hari_telat", judul: "Total hari telat" },
  { kunci: "hari_telat_terlama", judul: "Terlama" },
  { kunci: "po_batal_karena_telat", judul: "Batal karena telat" },
];

export default function VendorTelatPanel() {
  const navigate = useNavigate();
  const [telat, setTelat] = useState(null);
  const [batas, setBatas] = useState(7);
  const [vendor, setVendor] = useState(null);
  const [lihatVendor, setLihatVendor] = useState(false);
  const dTelat = useDaftar(telat || [], {
    awalan: "tl_", ukuran: 10, kolom: KOLOM_TELAT, kunciCari: ["po_number", "vendor_name"],
    urutAwal: { kunci: "hari_telat", arah: "desc" },
  });
  const dVendor = useDaftar(vendor || [], {
    awalan: "tv_", ukuran: 25, kolom: KOLOM_VENDOR, kunciCari: ["vendor_name"],
    urutAwal: { kunci: "total_hari_telat", arah: "desc" },
  });

  const muat = useCallback(async () => {
    try {
      const r = await daftarPOTelat();
      setTelat(r.data.items || []);
      setBatas(r.data.batas_hari_kerja ?? 7);
    } catch (e) {
      setTelat([]);
      toast.error(apiErrorMessage(e, "Daftar PO telat gagal dimuat."));
    }
  }, []);
  useEffect(() => { muat(); }, [muat]);

  const muatVendor = async () => {
    setLihatVendor((x) => !x);
    if (vendor) return;
    try {
      const r = await keterlambatanVendor();
      setVendor(r.data.vendor || []);
    } catch (e) {
      setVendor([]);
      toast.error(apiErrorMessage(e, "Keterlambatan per vendor gagal dimuat."));
    }
  };

  if (telat === null) return null;

  return (
    <Card className="p-4 space-y-3" data-testid="vendor-telat-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="font-medium flex items-center gap-2">
          <Clock className="h-4 w-4" /> Vendor telat ({telat.length} PO)
        </div>
        <Button size="sm" variant="ghost" onClick={muatVendor} data-testid="vendor-telat-per-vendor-btn">
          {lihatVendor ? "Tutup keterlambatan per vendor" : "Keterlambatan per vendor"}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Mulai sehari sesudah janji kirim: hubungi vendor. Mulai hari kerja ke-{batas + 1}, PO tanpa uang muka boleh
        diajukan batal ke Kepala Cabang dari halaman PO. Bila sebagian barang sudah diterima, yang dibatalkan hanya
        sisanya. PO ber-uang muka mengikuti aturan uang muka.
      </p>
      {telat.length === 0 ? (
        <div className="text-sm text-muted-foreground" data-testid="vendor-telat-kosong">Tidak ada PO yang telat.</div>
      ) : (
        <div className="overflow-x-auto">
          <PalangDaftar daftar={dTelat} placeholder="Cari nomor PO atau vendor…" testId="vendor-telat-palang"
                        ekspor={{ nama: "po_telat", kolom: KOLOM_TELAT }} />
          <table className="w-full text-sm" data-testid="vendor-telat-tabel">
            <thead>
              <tr className="text-left text-muted-foreground border-b">
                <ThUrut daftar={dTelat} kunci="po_number" className="py-1 pr-2">PO</ThUrut>
                <ThUrut daftar={dTelat} kunci="vendor_name" className="py-1 pr-2">Vendor</ThUrut>
                <ThUrut daftar={dTelat} kunci="janji_kirim" className="py-1 pr-2">Janji kirim</ThUrut>
                <ThUrut daftar={dTelat} kunci="hari_telat" align="right" className="py-1 pr-2 text-right">Telat</ThUrut>
                <th className="py-1">Keterangan</th>
              </tr>
            </thead>
            <tbody>
              {dTelat.tampil.length === 0 && (
                <tr><td colSpan={5} className="py-2 text-muted-foreground">Tidak ada yang cocok.</td></tr>
              )}
              {dTelat.tampil.map((t) => (
                <tr key={t.po_id} className="border-b last:border-0 hover:bg-muted/40 cursor-pointer"
                    onClick={() => navigate(`/purchasing/po/${t.po_id}`)} data-testid={`vendor-telat-${t.po_id}`}>
                  <td className="py-1 pr-2 font-mono">{t.po_number}</td>
                  <td className="py-1 pr-2">{t.vendor_name || "—"}</td>
                  <td className="py-1 pr-2">{formatDate(t.janji_kirim)}</td>
                  <td className="py-1 pr-2 text-right whitespace-nowrap">
                    {t.hari_telat} hari · {t.hari_kerja_telat} hari kerja
                  </td>
                  <td className="py-1 space-x-1">
                    {t.ber_uang_muka && <StatusBadge tone="neutral">Ber-uang muka</StatusBadge>}
                    {t.ada_penerimaan && <StatusBadge tone="neutral">Diterima sebagian</StatusBadge>}
                    {t.pengajuan_batal_menunggu && <StatusBadge tone="warning">Batal menunggu KC</StatusBadge>}
                    {t.boleh_ajukan_batal && (
                      <StatusBadge tone="danger">
                        {t.efek_batal === "TUTUP_SISA" ? "Sisa boleh diajukan batal" : "Boleh diajukan batal"}
                      </StatusBadge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Paginasi daftar={dTelat} testId="vendor-telat-paginasi" />
        </div>
      )}
      {lihatVendor && (
        <div className="overflow-x-auto border-t pt-3" data-testid="vendor-telat-per-vendor">
          {vendor === null ? (
            <div className="text-sm text-muted-foreground">Memuat…</div>
          ) : vendor.length === 0 ? (
            <div className="text-sm text-muted-foreground">Belum ada PO berjanji kirim yang bisa dinilai.</div>
          ) : (
            <>
            <PalangDaftar daftar={dVendor} placeholder="Cari vendor…" testId="vendor-telat-per-vendor-palang"
                          ekspor={{ nama: "keterlambatan_vendor", kolom: KOLOM_VENDOR }} />
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground border-b">
                  <ThUrut daftar={dVendor} kunci="vendor_name" className="py-1 pr-2">Vendor</ThUrut>
                  <ThUrut daftar={dVendor} kunci="po_dinilai" align="right" className="py-1 pr-2 text-right">PO dinilai</ThUrut>
                  <ThUrut daftar={dVendor} kunci="po_telat" align="right" className="py-1 pr-2 text-right">PO telat</ThUrut>
                  <ThUrut daftar={dVendor} kunci="total_hari_telat" align="right" className="py-1 pr-2 text-right">Total hari telat</ThUrut>
                  <ThUrut daftar={dVendor} kunci="hari_telat_terlama" align="right" className="py-1 pr-2 text-right">Terlama</ThUrut>
                  <ThUrut daftar={dVendor} kunci="po_batal_karena_telat" align="right" className="py-1 text-right">Batal karena telat</ThUrut>
                </tr>
              </thead>
              <tbody>
                {dVendor.tampil.map((v) => (
                  <tr key={v.vendor_id} className="border-b last:border-0">
                    <td className="py-1 pr-2">{v.vendor_name || "—"}</td>
                    <td className="py-1 pr-2 text-right">{v.po_dinilai}</td>
                    <td className="py-1 pr-2 text-right">{v.po_telat}{v.po_masih_telat > 0 && ` (${v.po_masih_telat} masih berjalan)`}</td>
                    <td className="py-1 pr-2 text-right">{v.total_hari_telat}</td>
                    <td className="py-1 pr-2 text-right">{v.hari_telat_terlama}</td>
                    <td className="py-1 text-right">{v.po_batal_karena_telat}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Paginasi daftar={dVendor} testId="vendor-telat-per-vendor-paginasi" />
            </>
          )}
          <p className="text-xs text-muted-foreground mt-2">
            Hari telat dihitung dari janji kirim sampai barang diterima lengkap (kiriman sebagian tidak menghentikan
            hitungan), tanggal tutup/batal, atau hari ini bila PO masih berjalan. PO tanpa janji kirim tidak dinilai.
          </p>
        </div>
      )}
    </Card>
  );
}
